import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  ReactNode,
} from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useUserProfile } from "@/hooks/useUserProfile";
import { toast } from "sonner";
import { FEATURES } from "@/config/features";
import { logActivity } from "@/hooks/useActivityLog";
import { translateToast } from "@/lib/i18n-toast";
import { getOrCreateDeviceId } from "@/lib/deviceId";

interface ZapierConnection {
  id: string;
  user_id: string;
  organization_id: string;
  token_expires_at: string;
  scopes: string[];
  zapier_email: string | null;
  created_at: string;
  updated_at: string;
}

interface ZapTemplate {
  name: string;
  trigger: {
    app_slug: string;
    event: string;
  };
  actions: Array<{
    app_slug: string;
    event: string;
    position: number;
  }>;
  fields?: {
    trigger?: Record<string, string>;
    [key: string]: Record<string, string> | undefined;
  };
}

interface MissingApp {
  appId: string;
  appSlug: string;
  title: string;
  image?: string;
  connectUrl?: string;
}

interface AvailableEvent {
  key: string;
  title: string;
  id: string;
}

interface CreateZapResult {
  success: boolean;
  zapId?: string;
  zapTitle?: string;
  editorUrl?: string;
  resolvedTemplate?: ZapTemplate;
  creationType?: "prefilled_url" | string;
  error?: string;
  message?: string;
  missingApps?: MissingApp[];
  // For action_not_found errors - show available events
  requestedEvent?: string;
  appSlug?: string;
  stepType?: "trigger" | "action";
  stepPosition?: number;
  availableEvents?: AvailableEvent[];
  suggestedMatch?: AvailableEvent | null;
}

interface ZapierCreatePrecheckResult {
  success: boolean;
  ready?: boolean;
  error?: string;
  message?: string;
}

type MatchType =
  | "exact"
  | "trigger_only"
  | "step_fingerprint"
  | "title_fuzzy"
  | "recent_activity";

interface FindZapCandidate {
  editorUrl?: string;
  zapId?: string | null;
  zapTitle?: string | null;
  confidence?: number;
  matchType?: MatchType;
}

interface FindZapResult {
  success: boolean;
  editorUrl?: string;
  zapId?: string;
  zapTitle?: string;
  source?: "cached" | "api_lookup";
  // Confidence scoring for match quality
  confidence?: number; // 0-1 where 1 is exact match
  matchType?: MatchType;
  totalZapsSearched?: number;
  error?: string;
  message?: string;
  candidate?: FindZapCandidate;
}

interface ZapierAuthContextType {
  isConnected: boolean;
  isLoading: boolean;
  connection: ZapierConnection | null;
  connectZapier: () => Promise<void>;
  disconnectZapier: () => Promise<void>;
  precheckCreateZap: () => Promise<ZapierCreatePrecheckResult>;
  createZap: (
    zapTemplate?: ZapTemplate | null,
    guideId?: string,
  ) => Promise<CreateZapResult>;
  findZap: (guideId: string) => Promise<FindZapResult>;
  refreshConnection: () => Promise<void>;
}

function isReconnectRequiredError(error?: string): boolean {
  return error === "not_connected" || error === "reauth_required";
}

async function parseFunctionInvokeError(error: unknown): Promise<{
  error: string;
  message: string;
}> {
  const fallbackMessage =
    error instanceof Error ? error.message : "Request failed";
  const response = (error as { context?: Response } | null)?.context;

  if (response) {
    try {
      const body = (await response.clone().json()) as {
        error?:
          | string
          | {
              code?: string;
              message?: string;
            };
        message?: string;
      } | null;

      const errorCode =
        typeof body?.error === "string"
          ? body.error
          : body?.error?.code || "api_error";
      const errorMessage =
        body?.message ||
        (typeof body?.error === "string" ? body.error : body?.error?.message) ||
        fallbackMessage;

      return { error: errorCode, message: errorMessage };
    } catch {
      try {
        const text = (await response.clone().text()).trim();
        if (text) {
          return { error: "api_error", message: text };
        }
      } catch {
        // Ignore parsing errors and use fallback below
      }
    }

    return {
      error: "api_error",
      message: `${fallbackMessage} (HTTP ${response.status})`,
    };
  }

  return { error: "api_error", message: fallbackMessage };
}

const ZapierAuthContext = createContext<ZapierAuthContextType | null>(null);

export function ZapierAuthProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { organization } = useUserProfile();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [connection, setConnection] = useState<ZapierConnection | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refreshConnection = useCallback(async () => {
    if (!user) {
      setConnection(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const deviceId = getOrCreateDeviceId();
      const { data, error } = await supabase
        .from("zapier_connections")
        .select(
          "id, user_id, organization_id, token_expires_at, scopes, zapier_email, created_at, updated_at",
        )
        .eq("user_id", user.id)
        .maybeSingle();

      if (error) {
        console.error("Error fetching Zapier connection:", error);
        setConnection(null);
      } else if (!data) {
        setConnection(null);
      } else {
        // Enforce reconnect on new devices. If this browser was never used to
        // connect Zapier, treat as disconnected until OAuth completes.
        if (!deviceId) {
          setConnection(data);
          return;
        }

        const { data: trustedDevice, error: trustedDeviceError } =
          await supabase
            .from("zapier_connection_devices")
            .select("id, last_seen_at")
            .eq("user_id", user.id)
            .eq("device_id", deviceId)
            .maybeSingle();

        if (trustedDeviceError) {
          console.error(
            "Error fetching trusted Zapier device state:",
            trustedDeviceError,
          );
          setConnection(data);
          return;
        }

        if (!trustedDevice) {
          const { data: anyTrustedDevice, error: anyTrustedDeviceError } =
            await supabase
              .from("zapier_connection_devices")
              .select("id")
              .eq("user_id", user.id)
              .limit(1)
              .maybeSingle();

          if (anyTrustedDeviceError) {
            console.error(
              "Error checking trusted Zapier devices:",
              anyTrustedDeviceError,
            );
            setConnection(data);
            return;
          }

          // Backward compatibility for legacy Zapier connections created before
          // trusted-device enforcement existed.
          if (!anyTrustedDevice && deviceId) {
            const { error: bootstrapTrustError } = await supabase
              .from("zapier_connection_devices")
              .upsert(
                {
                  user_id: user.id,
                  organization_id: data.organization_id,
                  device_id: deviceId,
                  last_seen_at: new Date().toISOString(),
                },
                { onConflict: "user_id,device_id" },
              );

            if (bootstrapTrustError) {
              console.error(
                "Failed to bootstrap trusted Zapier device for legacy connection:",
                bootstrapTrustError,
              );
            }

            setConnection(data);
            return;
          }

          setConnection(null);
          return;
        }

        setConnection(data);

        // Best-effort heartbeat for trusted devices.
        void supabase
          .from("zapier_connection_devices")
          .update({ last_seen_at: new Date().toISOString() })
          .eq("id", trustedDevice.id);
      }
    } catch (err) {
      console.error("Error fetching Zapier connection:", err);
      setConnection(null);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  // Check for OAuth callback parameters
  useEffect(() => {
    const zapierConnected = searchParams.get("zapier_connected");
    const zapierError = searchParams.get("zapier_error");
    const zapierErrorDesc = searchParams.get("zapier_error_description");
    const zapierDuplicate = searchParams.get("zapier_duplicate");
    const zapierEmail = searchParams.get("zapier_email");

    if (zapierConnected === "true") {
      // Show duplicate warning if this Zapier account was already connected elsewhere
      if (zapierDuplicate === "true" && zapierEmail) {
        toast.warning(
          translateToast("toast.zapier.duplicateWarning", {
            email: zapierEmail,
          }),
          {
            duration: 10000, // Show longer for important warning
          },
        );
      } else {
        toast.success(translateToast("toast.zapier.connected"));
      }
      // Clear URL params
      searchParams.delete("zapier_connected");
      searchParams.delete("zapier_duplicate");
      searchParams.delete("zapier_email");
      setSearchParams(searchParams, { replace: true });
      // Log activity for audit trail
      logActivity({
        action: "zapier_connected",
        details:
          zapierDuplicate === "true"
            ? { duplicate_warning_shown: true }
            : undefined,
      });
      // Refresh connection
      refreshConnection();
    } else if (zapierError) {
      const errorMessage = zapierErrorDesc || getErrorMessage(zapierError);
      toast.error(
        translateToast("toast.zapier.connectionFailed", {
          error: errorMessage,
        }),
      );
      // Clear URL params
      searchParams.delete("zapier_error");
      searchParams.delete("zapier_error_description");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, t, refreshConnection]);

  // Load connection on mount
  useEffect(() => {
    if (user) {
      refreshConnection();
    } else {
      setConnection(null);
      setIsLoading(false);
    }
  }, [user, refreshConnection]);

  const connectZapier = useCallback(async () => {
    if (!user || !organization) {
      toast.error(translateToast("toast.zapier.loginFirst"));
      return;
    }

    try {
      const deviceId = getOrCreateDeviceId();
      // Pass current URL so OAuth callback redirects back to the same page
      // This preserves the guide ID for automation guides
      const returnUrl = window.location.href;
      const { data, error } = await supabase.functions.invoke(
        "zapier-get-auth-url",
        {
          body: {
            organizationId: organization.id,
            returnUrl,
            ...(deviceId ? { deviceId } : {}),
          },
        },
      );

      if (error) {
        throw error;
      }

      if (data?.authUrl) {
        // Redirect to Zapier OAuth
        window.location.href = data.authUrl;
      } else {
        throw new Error("No auth URL returned");
      }
    } catch (err) {
      console.error("Error starting Zapier OAuth:", err);
      toast.error(translateToast("toast.zapier.startFailed"));
    }
  }, [user, organization, t]);

  const disconnectZapier = useCallback(async () => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from("zapier_connections")
        .delete()
        .eq("user_id", user.id);

      if (error) {
        throw error;
      }

      setConnection(null);
      toast.success(translateToast("toast.zapier.disconnected"));
    } catch (err) {
      console.error("Error disconnecting Zapier:", err);
      toast.error(translateToast("toast.zapier.disconnectFailed"));
    }
  }, [user, t]);

  const precheckCreateZap =
    useCallback(async (): Promise<ZapierCreatePrecheckResult> => {
      if (!user || !organization) {
        return {
          success: false,
          error: "not_authenticated",
          message: "Please log in first",
        };
      }

      try {
        const response = await supabase.functions.invoke("zapier-create-zap", {
          body: {
            organizationId: organization.id,
            preflightOnly: true,
          },
        });

        const { data, error } = response;
        console.log("zapier-create-zap preflight response:", { data, error });

        if (data && typeof data === "object") {
          const typed = data as ZapierCreatePrecheckResult;
          if (!typed.success && isReconnectRequiredError(typed.error)) {
            await refreshConnection();
          }
          return typed;
        }

        if (error) {
          console.log("Zapier precheck transport error:", error);
          const parsedError = await parseFunctionInvokeError(error);
          if (isReconnectRequiredError(parsedError.error)) {
            await refreshConnection();
          }
          return {
            success: false,
            error: parsedError.error,
            message: parsedError.message,
          };
        }

        return {
          success: false,
          error: "unknown_error",
          message: "No response from server",
        };
      } catch (err) {
        console.error("Error running Zapier precheck:", err);
        return {
          success: false,
          error: "unexpected_error",
          message:
            err instanceof Error ? err.message : "An unexpected error occurred",
        };
      }
    }, [user, organization, refreshConnection]);

  const createZap = useCallback(
    async (
      zapTemplate?: ZapTemplate | null,
      guideId?: string,
    ): Promise<CreateZapResult> => {
      if (!user || !organization) {
        return {
          success: false,
          error: "not_authenticated",
          message: "Please log in first",
        };
      }

      if (!connection) {
        return {
          success: false,
          error: "not_connected",
          message: "Please connect your Zapier account first",
        };
      }

      try {
        console.log("Calling zapier-create-zap", {
          hasTemplate: !!zapTemplate,
          templateName: zapTemplate?.name || null,
          guideId: guideId || null,
        });
        const response = await supabase.functions.invoke("zapier-create-zap", {
          body: {
            ...(zapTemplate ? { zapTemplate } : {}),
            guideId,
            organizationId: organization.id,
          },
        });

        const { data, error } = response;
        console.log("zapier-create-zap response:", { data, error });

        // Edge function now returns 200 with success: false for app errors
        // so we can read the actual error details from data
        if (data && typeof data === "object") {
          const typed = data as CreateZapResult;
          if (!typed.success && isReconnectRequiredError(typed.error)) {
            await refreshConnection();
          }
          return typed;
        }

        // Fallback for actual transport/auth errors
        if (error) {
          console.log("Zapier create-zap transport error:", error);
          const parsedError = await parseFunctionInvokeError(error);
          if (isReconnectRequiredError(parsedError.error)) {
            await refreshConnection();
          }
          return {
            success: false,
            error: parsedError.error,
            message: parsedError.message,
          };
        }

        return {
          success: false,
          error: "unknown_error",
          message: "No response from server",
        };
      } catch (err) {
        console.error("Error creating Zap:", err);
        return {
          success: false,
          error: "unexpected_error",
          message:
            err instanceof Error ? err.message : "An unexpected error occurred",
        };
      }
    },
    [user, organization, connection, refreshConnection],
  );

  const findZap = useCallback(
    async (guideId: string): Promise<FindZapResult> => {
      if (!user || !organization) {
        return {
          success: false,
          error: "not_authenticated",
          message: "Please log in first",
        };
      }

      if (!connection) {
        return {
          success: false,
          error: "not_connected",
          message: "Please connect your Zapier account first",
        };
      }

      try {
        const invokeFindZap = async () => {
          console.log("Calling zapier-find-zap for guide:", guideId);
          const response = await supabase.functions.invoke("zapier-find-zap", {
            body: {
              guideId,
              organizationId: organization.id,
            },
          });

          const { data, error } = response;
          console.log("zapier-find-zap response:", { data, error });
          return response;
        };

        const parseFindZapResponse = async (
          response: Awaited<ReturnType<typeof invokeFindZap>>,
        ): Promise<FindZapResult> => {
          const { data, error } = response;

          if (data && typeof data === "object") {
            return data as FindZapResult;
          }

          if (error) {
            console.log("Zapier find-zap transport error:", error);
            const parsedError = await parseFunctionInvokeError(error);
            return {
              success: false,
              error: parsedError.error,
              message: parsedError.message,
            };
          }

          return {
            success: false,
            error: "unknown_error",
            message: "No response from server",
          };
        };

        let result = await parseFindZapResponse(await invokeFindZap());

        // Session JWT can expire while app is open; refresh and retry once.
        if (!result.success && result.error === "not_authenticated") {
          console.warn(
            "Find Zap returned not_authenticated, attempting session refresh",
          );

          const { data: refreshData, error: refreshError } =
            await supabase.auth.refreshSession();

          if (!refreshError && refreshData.session) {
            result = await parseFindZapResponse(await invokeFindZap());
          } else {
            console.error(
              "Failed to refresh session for Find Zap:",
              refreshError,
            );
          }
        }

        return result;
      } catch (err) {
        console.error("Error finding Zap:", err);
        return {
          success: false,
          error: "unexpected_error",
          message:
            err instanceof Error ? err.message : "An unexpected error occurred",
        };
      }
    },
    [user, organization, connection],
  );

  const contextValue = useMemo(
    () => ({
      isConnected: !!connection,
      isLoading,
      connection,
      connectZapier,
      disconnectZapier,
      precheckCreateZap,
      createZap,
      findZap,
      refreshConnection,
    }),
    [
      connection,
      isLoading,
      connectZapier,
      disconnectZapier,
      precheckCreateZap,
      createZap,
      findZap,
      refreshConnection,
    ],
  );

  return (
    <ZapierAuthContext.Provider value={contextValue}>
      {children}
    </ZapierAuthContext.Provider>
  );
}

// Default stub for when ZAPIER_PARTNER_API is disabled
const disabledStub: ZapierAuthContextType = {
  isConnected: false,
  isLoading: false,
  connection: null,
  connectZapier: async () => {
    console.warn("Zapier Partner API is disabled");
  },
  disconnectZapier: async () => {
    console.warn("Zapier Partner API is disabled");
  },
  precheckCreateZap: async () => ({
    success: false,
    error: "feature_disabled",
    message: "Zapier Partner API is not enabled",
  }),
  createZap: async () => ({
    success: false,
    error: "feature_disabled",
    message: "Zapier Partner API is not enabled",
  }),
  findZap: async () => ({
    success: false,
    error: "feature_disabled",
    message: "Zapier Partner API is not enabled",
  }),
  refreshConnection: async () => {},
};

export function useZapierAuth() {
  const context = useContext(ZapierAuthContext);

  // When ZAPIER_PARTNER_API is disabled, return a safe stub
  // This prevents errors when the provider is not rendered
  if (!FEATURES.ZAPIER_PARTNER_API) {
    return disabledStub;
  }

  if (!context) {
    throw new Error("useZapierAuth must be used within a ZapierAuthProvider");
  }
  return context;
}

function getErrorMessage(error: string): string {
  switch (error) {
    case "access_denied":
      return "Access was denied. Please try again.";
    case "missing_parameters":
      return "Invalid OAuth callback. Please try again.";
    case "invalid_state":
      return "Security validation failed. Please try again.";
    case "state_expired":
      return "OAuth session expired. Please try again.";
    case "token_exchange_failed":
      return "Failed to complete authorization. Please try again.";
    case "storage_failed":
      return "Failed to save connection. Please try again.";
    default:
      return "An unexpected error occurred.";
  }
}

export type {
  ZapTemplate,
  MissingApp,
  ZapierCreatePrecheckResult,
  CreateZapResult,
  FindZapResult,
  ZapierConnection,
  AvailableEvent,
};
